import ScrollReveal from "@/components/ScrollReveal";
import { ExternalLink } from "lucide-react";

const stats = [
  { value: "190", label: "Nations Assessed" },
  { value: "47", label: "Published Papers" },
  { value: "12", label: "Policy Partnerships" },
  { value: "G20", label: "Advisory Status" },
];

const recognizedBy = [
  "World Economic Forum",
  "Bank for International Settlements",
  "Financial Stability Board",
  "G20 Digital Economy Task Force",
];

export default function TrustSection() {
  return (
    <section
      id="trust"
      data-testid="trust-section"
      className="py-28 md:py-36 lg:py-44 px-6 md:px-12 bg-[#0A1325] border-y border-slate-800 relative overflow-hidden"
    >
      {/* Subtle texture */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]"
        style={{ background: "radial-gradient(ellipse at 50% 0%, #D4AF37, transparent 60%)" }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          {/* Left editorial column */}
          <div className="lg:col-span-7">
            <ScrollReveal>
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-sans mb-4">
                Research & Advocacy
              </p>
              <h2
                data-testid="trust-heading"
                className="font-serif text-3xl md:text-4xl font-bold text-white tracking-tight mb-8"
              >
                JCAM Non-Profit
                <br />
                Research Division
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <div className="border-l-2 border-[#D4AF37]/40 pl-6 mb-10">
                <blockquote className="font-serif text-lg md:text-xl text-slate-300 leading-relaxed italic">
                  "Data sovereignty is not a feature — it is a fundamental right.
                  Our research ensures that right is protected for institutions
                  and individuals alike."
                </blockquote>
                <p className="text-xs text-slate-500 mt-4 tracking-wide uppercase">
                  — J. Carter, Founder & Chairman
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <p className="text-sm text-slate-400 leading-relaxed mb-8">
                The JCAM Non-Profit Research Division publishes the{" "}
                <span className="text-white font-medium">
                  Global Data Sovereignty Index
                </span>
                , an annual benchmark assessing 190 nations on data protection,
                digital asset regulation, and infrastructure resilience. Our
                findings directly inform policy at the highest levels of international governance.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <div className="mb-10">
                <p className="text-[10px] tracking-[0.2em] uppercase text-slate-600 mb-4">
                  Recognized By
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {recognizedBy.map((org) => (
                    <span key={org} className="text-xs text-slate-400">
                      {org}
                    </span>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.35}>
              <a
                data-testid="sovereignty-index-link"
                href="#"
                className="inline-flex items-center gap-2 text-xs text-[#D4AF37] hover:text-[#F0CA4E] tracking-[0.15em] uppercase transition-colors duration-300"
              >
                View the Global Data Sovereignty Index
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </ScrollReveal>
          </div>

          {/* Right stats column */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="grid grid-cols-2 gap-0 border border-slate-800">
              {stats.map((stat, i) => (
                <ScrollReveal key={stat.label} delay={0.2 + i * 0.1}>
                  <div
                    data-testid={`trust-stat-${i}`}
                    className={`p-8 md:p-10 text-center ${
                      i < 2 ? "border-b border-slate-800" : ""
                    } ${i % 2 === 0 ? "border-r border-slate-800" : ""}`}
                  >
                    <span className="block font-serif text-3xl md:text-4xl font-bold text-[#D4AF37]">
                      {stat.value}
                    </span>
                    <span className="text-[10px] tracking-[0.15em] uppercase text-slate-500 mt-2 block">
                      {stat.label}
                    </span>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
